import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { GoogleOAuthProvider } from "@react-oauth/google";
import DashboardLayout from "@/components/DashboardLayout";
import InstallPrompt from "@/components/InstallPrompt";
import AssistantWidget from "@/components/AssistantWidget";
import SplashScreen from "@/components/SplashScreen";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import AuthCallback from "@/pages/AuthCallback";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Teams from "@/pages/Teams";
import Players from "@/pages/Players";
import PlayerProfile from "@/pages/PlayerProfile";
import Attendance from "@/pages/Attendance";
import Performance from "@/pages/Performance";
import Training from "@/pages/Training";
import Matches from "@/pages/Matches";
import Rankings from "@/pages/Rankings";
import Payments from "@/pages/Payments";
import Messages from "@/pages/Messages";
import Registrations from "@/pages/Registrations";
import Reports from "@/pages/Reports";
import AICoach from "@/pages/AICoach";
import TacticBoard from "@/pages/TacticBoard";
import Library from "@/pages/Library";import Settings from "@/pages/Settings";
import Admin from "@/pages/Admin";
import PublicRegister from "@/pages/PublicRegister";
import ClubSite from "@/pages/ClubSite";
import PaymentResult from "@/pages/PaymentResult";
import ParentPortal from "@/pages/ParentPortal";
import PlayerPortal from "@/pages/PlayerPortal";
import PayFee from "@/pages/PayFee";
import Community from "@/pages/Community";
import InterClub from "@/pages/InterClub";
import TournamentInvite from "@/pages/TournamentInvite";
import PublicTournament from "@/pages/PublicTournament";
import LiveMatch from "@/pages/LiveMatch";
import StandingsOverlay from "@/pages/StandingsOverlay";
import StaffPage from "@/pages/StaffPage";
import StaffInvite from "@/pages/StaffInvite";
import Officials from "@/pages/Officials";
import OfficialInvite from "@/pages/OfficialInvite";
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import Terms from "@/pages/legal/Terms";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import BlogAdmin from "@/pages/BlogAdmin";
import PlatformLayout from "@/components/PlatformLayout";
import PlatformAdmins from "@/pages/PlatformAdmins";

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="h-10 w-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function Protected({ children, requireClub = true }) {
  const { user, club } = useAuth();
  if (user === undefined) return <Loading />;
  if (user === null) return <Navigate to="/login" replace />;
  if (user.role === "parent") return <Navigate to="/parent" replace />;
  if (user.role === "player") return <Navigate to="/portal" replace />;
  if (user.role === "official") return <Navigate to="/officials" replace />;
  if (user.role === "admin") return <Navigate to="/platform" replace />;
  if (requireClub && !club && user.role !== "admin") return <Navigate to="/onboarding" replace />;
  return children;
}

function ParentProtected({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <Loading />;
  if (user === null) return <Navigate to="/login" replace />;
  if (user.role === "player") return <Navigate to="/portal" replace />;
  if (user.role !== "parent") return <Navigate to="/app" replace />;
  return children;
}

function PlayerProtected({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <Loading />;
  if (user === null) return <Navigate to="/login" replace />;
  if (user.role !== "player") return <Navigate to={user.role === "parent" ? "/parent" : "/app"} replace />;
  return children;
}

function OfficialProtected({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <Loading />;
  if (user === null) return <Navigate to="/login" replace />;
  if (user.role !== "official") return <Navigate to="/app" replace />;
  return children;
}

function PlatformProtected({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <Loading />;
  if (user === null) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/app" replace />;
  return children;
}

function GuestOnly({ children }) {  const { user, club } = useAuth();
  if (user === undefined) return <Loading />;
  if (user) {
    if (user.role === "parent") return <Navigate to="/parent" replace />;
    if (user.role === "player") return <Navigate to="/portal" replace />;
    if (user.role === "official") return <Navigate to="/officials" replace />;
    return <Navigate to={user.role === "admin" ? "/platform" : club ? "/app" : "/onboarding"} replace />;
  }
  return children;
}

function Root() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<GuestOnly><Login /></GuestOnly>} />
      <Route path="/register" element={<GuestOnly><Register /></GuestOnly>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/join/:clubId" element={<PublicRegister />} />
      <Route path="/club/:clubId" element={<ClubSite />} />
      <Route path="/tournament-invite/:token" element={<TournamentInvite />} />
      <Route path="/tournament/:id" element={<PublicTournament />} />
      <Route path="/tournament/:id/match/:mid" element={<LiveMatch />} />
      <Route path="/tournament/:id/overlay" element={<StandingsOverlay />} />
      <Route path="/staff-invite/:token" element={<StaffInvite />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/pay/:feeId" element={<PayFee />} />
      <Route path="/payment/success" element={<PaymentResult />} />
      <Route path="/payment/cancel" element={<PaymentResult />} />
      <Route path="/onboarding" element={<Protected requireClub={false}><Onboarding /></Protected>} />
      <Route path="/parent" element={<ParentProtected><ParentPortal /></ParentProtected>} />
      <Route path="/portal" element={<PlayerProtected><PlayerPortal /></PlayerProtected>} />
      <Route path="/officials" element={<OfficialProtected><Officials /></OfficialProtected>} />
      <Route path="/official-invite/:token" element={<OfficialInvite />} />
      <Route path="/app" element={<Protected><DashboardLayout /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="teams" element={<Teams />} />
        <Route path="players" element={<Players />} />
        <Route path="players/:id" element={<PlayerProfile />} />
        <Route path="attendance" element={<Attendance />} />
        <Route path="performance" element={<Performance />} />
        <Route path="training" element={<Training />} />
        <Route path="matches" element={<Matches />} />
        <Route path="rankings" element={<Rankings />} />
        <Route path="tournaments" element={<InterClub />} />
        <Route path="staff" element={<StaffPage />} />
        <Route path="payments" element={<Payments />} />
        <Route path="messages" element={<Messages />} />
        <Route path="registrations" element={<Registrations />} />
        <Route path="reports" element={<Reports />} />
        <Route path="ai" element={<AICoach />} />
        <Route path="tactics" element={<TacticBoard />} />
        <Route path="library" element={<Library />} />
        <Route path="blog" element={<BlogAdmin />} />
        <Route path="community" element={<Community />} />
        <Route path="settings" element={<Settings />} />
      </Route>
      <Route path="/platform" element={<PlatformProtected><PlatformLayout /></PlatformProtected>}>
        <Route index element={<Admin />} />
        <Route path="blog" element={<BlogAdmin />} />
        <Route path="admins" element={<PlatformAdmins />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ""}>
        <AuthProvider>
          <BrowserRouter>
            <SplashScreen />
            <Root />
            <InstallPrompt />
            <AssistantWidget />
            <Toaster position="top-right" theme="dark" richColors />
          </BrowserRouter>
        </AuthProvider>
      </GoogleOAuthProvider>
    </div>
  );
}
